import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ChevronRight, Search, X, Edit2, IndianRupee, Trash2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  national_partner:  "bg-violet-500/10 text-violet-600 border-violet-500/20",
  regional_partner:  "bg-blue-500/10 text-blue-600 border-blue-500/20",
  zonal_partner:     "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  sales_manager:     "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  sales_partner:     "bg-amber-500/10 text-amber-600 border-amber-500/20",
};
const LEVEL_LABELS = {
  national_partner: "National Partner",
  regional_partner: "Regional Partner",
  zonal_partner:    "Zonal Partner",
  sales_manager:    "Sales Manager",
  sales_partner:    "Sales Partner",
};
const LEVELS = ["national_partner", "regional_partner", "zonal_partner", "sales_manager", "sales_partner"];

const STATUS_COLORS = {
  active:  "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  inactive:"bg-amber-500/10 text-amber-600 border-amber-500/20",
  kyc_pending: "bg-gray-500/10 text-gray-500 border-gray-500/20",
};

const TypeBadge = ({ type }) => (
  <span className={`text-[9px] uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 font-semibold ${LEVEL_COLORS[type] || "bg-muted text-muted-foreground border-border"}`}>
    {LEVEL_LABELS[type] || type}
  </span>
);

const inp = "w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50";
const lbl = "text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1";

function TreeNode({ node, depth, hasChildren, children, onEdit }) {
  const [open, setOpen] = useState(depth < 2);
  return (
    <div className="select-none">
      <div
        className="flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-muted/50 group"
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        <button
          onClick={() => hasChildren && setOpen(o => !o)}
          className="w-4 h-4 shrink-0 flex items-center justify-center"
          aria-label="Toggle"
        >
          {hasChildren
            ? <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
            : <span className="w-2 h-2 rounded-full bg-border/60" />
          }
        </button>
        <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
          {(node.name || "?")[0].toUpperCase()}
        </div>
        <span className="text-[13px] text-foreground font-medium truncate flex-1">{node.name}</span>
        <TypeBadge type={node.partner_type} />
        <button
          onClick={() => onEdit(node)}
          data-testid={`edit-hierarchy-${node.id}`}
          title="Edit partner"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all shrink-0 opacity-60 group-hover:opacity-100 sm:opacity-100"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && hasChildren && children}
    </div>
  );
}

function renderTree(nodes, depth = 0, onEdit) {
  return nodes.map(node => {
    const hasChildren = node.children?.length > 0;
    return (
      <TreeNode key={node.id} node={node} depth={depth} hasChildren={hasChildren} onEdit={onEdit}>
        {hasChildren && renderTree(node.children, depth + 1, onEdit)}
      </TreeNode>
    );
  });
}

const EMPTY = { name: "", email: "", phone: "", partner_type: "sales_partner", parent_id: "", region: "", zone: "", tds_rate: 10, pan_category: "individual" };

// Shared partner form fields used in both Add and Edit modals
function PartnerFormFields({ form, setForm, flat, isEdit }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className={lbl}>Name *</label>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className={inp} data-testid="hierarchy-name-input" />
      </div>
      <div>
        <label className={lbl}>Email *</label>
        <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className={inp} data-testid="hierarchy-email-input" />
      </div>
      <div>
        <label className={lbl}>Phone</label>
        <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inp} />
      </div>
      <div>
        <label className={lbl}>Partner Level *</label>
        <select value={form.partner_type} onChange={e => setForm(f => ({ ...f, partner_type: e.target.value }))} className={inp}>
          {LEVELS.map(l => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
        </select>
      </div>
      <div>
        <label className={lbl}>Reports To (Parent)</label>
        <select value={form.parent_id} onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))} className={inp}>
          <option value="">— None (top-level) —</option>
          {flat
            .filter(p => {
              if (isEdit && p.id === form._id) return false; // exclude self
              return LEVELS.indexOf(p.partner_type) < LEVELS.indexOf(form.partner_type);
            })
            .map(p => <option key={p.id} value={p.id}>{p.name} ({LEVEL_LABELS[p.partner_type]})</option>)
          }
        </select>
      </div>
      <div>
        <label className={lbl}>TDS Rate (%)</label>
        <input type="number" value={form.tds_rate} onChange={e => setForm(f => ({ ...f, tds_rate: parseFloat(e.target.value) || 0 }))}
          min={0} max={30} step={0.5} className={inp} />
      </div>
      <div>
        <label className={lbl}>Region</label>
        <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} className={inp} />
      </div>
      <div>
        <label className={lbl}>PAN Category</label>
        <select value={form.pan_category} onChange={e => setForm(f => ({ ...f, pan_category: e.target.value }))} className={inp}>
          <option value="individual">Individual</option>
          <option value="company">Company</option>
          <option value="firm">Firm/LLP</option>
        </select>
      </div>
      {isEdit && (
        <div>
          <label className={lbl}>Status</label>
          <select value={form.status || "active"} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inp}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      )}
    </div>
  );
}

export default function SMHierarchy() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list"); // list default on mobile; tree for desktop
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editPartner, setEditPartner] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const [treeRes, flatRes] = await Promise.all([
        axios.get(`${API}/api/sm/hierarchy/tree`, { withCredentials: true }),
        axios.get(`${API}/api/sm/hierarchy`, { withCredentials: true }),
      ]);
      setTree(treeRes.data.tree || []);
      setFlat(flatRes.data.partners || []);
    } catch { toast.error("Failed to load hierarchy"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const filtered = flat.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/sm/hierarchy`, { ...form, parent_id: form.parent_id || null }, { withCredentials: true });
      toast.success(`${LEVEL_LABELS[form.partner_type]} added`);
      setShowAdd(false);
      setForm(EMPTY);
      loadTree();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add partner");
    } finally { setSaving(false); }
  };

  const openEdit = (p) => {
    setEditPartner(p);
    setEditForm({
      _id: p.id,
      name: p.name || "",
      email: p.email || "",
      phone: p.phone || "",
      partner_type: p.partner_type || "sales_partner",
      parent_id: p.parent_id || "",
      region: p.region || "",
      tds_rate: p.tds_rate ?? 10,
      pan_category: p.pan_category || "individual",
      status: p.status || "active",
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const { _id, ...payload } = editForm;
      await axios.patch(
        `${API}/api/sm/partners/${editPartner.id}`,
        { ...payload, parent_id: payload.parent_id || null },
        { withCredentials: true }
      );
      toast.success("Partner updated");
      setEditPartner(null);
      loadTree();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update partner");
    } finally { setEditSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archive this partner? They will be marked as deleted but records are kept.")) return;
    try {
      await axios.delete(`${API}/api/sm/partners/${id}`, { withCredentials: true });
      toast.success("Partner archived"); loadTree();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to archive"); }
  };

  const handlePurge = async (id, name) => {
    if (!window.confirm(`PERMANENTLY DELETE "${name}" and all related records? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/sm/partners/${id}/purge`, { withCredentials: true });
      toast.success("Partner permanently deleted"); loadTree();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to purge"); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-hierarchy">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-foreground">Partner Network</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{flat.length} partners across {LEVELS.length} levels</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-0.5 text-[11px]">
            {["Tree", "List"].map(v => (
              <button key={v} onClick={() => setView(v.toLowerCase())}
                className={`px-3 py-1 rounded-md capitalize transition-all ${view === v.toLowerCase() ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-partner-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-[13px] font-medium hover:opacity-90 transition-all">
            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add</span> Partner
          </button>
        </div>
      </div>

      {/* Level legend */}
      <div className="flex flex-wrap gap-2">
        {LEVELS.map(l => <TypeBadge key={l} type={l} />)}
      </div>

      {/* Search (list view) */}
      {view === "list" && (
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="w-full pl-8 pr-3 py-2 bg-card border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
        </div>
      )}

      {/* Main view */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm animate-pulse">Loading network…</div>
        ) : view === "tree" ? (
          <div className="p-3">
            {tree.length === 0
              ? <div className="py-12 text-center text-muted-foreground text-sm">No partners yet. Add your National Partner first.</div>
              : renderTree(tree, 0, openEdit)
            }
          </div>
        ) : (
          <>
            {filtered.length === 0 && (
              <div className="py-10 text-center text-muted-foreground text-sm">No partners found</div>
            )}

            {/* ── Mobile card view (sm and below) ── */}
            <div className="sm:hidden divide-y divide-border">
              {filtered.map(p => (
                <div key={p.id} className="p-4 flex items-start justify-between gap-3" data-testid={`hierarchy-card-${p.id}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-sm font-bold text-primary shrink-0 mt-0.5">
                      {(p.name || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                      {p.phone && <p className="text-xs text-muted-foreground">{p.phone}</p>}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <TypeBadge type={p.partner_type} />
                        <span className={`text-[9px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLORS[p.status] || "bg-muted text-muted-foreground border-border"}`}>
                          {p.status}
                        </span>
                        <span className="text-[10px] text-muted-foreground">TDS {p.tds_rate ?? 10}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                    <button onClick={() => openEdit(p)} data-testid={`edit-hierarchy-mobile-${p.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                    {isSuperAdmin && (
                      <>
                        <button onClick={() => handleDelete(p.id)} data-testid={`delete-hierarchy-mobile-${p.id}`} title="Archive partner"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handlePurge(p.id, p.name)} data-testid={`purge-hierarchy-mobile-${p.id}`} title="Permanently delete"
                          className="p-1.5 rounded-lg text-muted-foreground/50 hover:text-red-700 hover:bg-red-500/10 transition-all">
                          <ShieldAlert className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Desktop table view (sm and above) ── */}
            <table className="w-full hidden sm:table text-[13px]">
              <thead className="border-b border-border bg-muted/30">
                <tr>
                  {["Name", "Email", "Level", "Status", "TDS %", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{p.email}</td>
                    <td className="px-4 py-3"><TypeBadge type={p.partner_type} /></td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLORS[p.status] || "bg-muted text-muted-foreground border-border"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.tds_rate ?? 10}%</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => openEdit(p)} data-testid={`edit-hierarchy-${p.id}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-primary/30 text-primary hover:bg-primary/10 transition-all">
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        {isSuperAdmin && (
                          <>
                            <button onClick={() => handleDelete(p.id)} data-testid={`delete-hierarchy-${p.id}`} title="Archive partner"
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handlePurge(p.id, p.name)} data-testid={`purge-hierarchy-${p.id}`} title="Permanently delete all records"
                              className="p-1.5 rounded-lg text-muted-foreground/50 hover:text-red-700 hover:bg-red-500/10 transition-all">
                              <ShieldAlert className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-muted-foreground text-sm">No partners found</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ── Add Partner modal ── */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="text-[15px] font-semibold">Add Partner to Network</div>
              <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="flex-1 overflow-y-auto p-6 space-y-4">
              <PartnerFormFields form={form} setForm={setForm} flat={flat} isEdit={false} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)}
                  className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="save-partner-btn"
                  className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
                  {saving ? "Adding…" : "Add Partner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Partner modal ── */}
      {editPartner && editForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div>
                <div className="text-[15px] font-semibold">Edit Partner</div>
                <div className="text-xs text-muted-foreground mt-0.5">{editPartner.name}</div>
              </div>
              <button onClick={() => setEditPartner(null)} data-testid="edit-hierarchy-modal-close" className="text-muted-foreground hover:text-foreground p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="flex-1 overflow-y-auto p-6 space-y-4">
              <PartnerFormFields form={editForm} setForm={setEditForm} flat={flat} isEdit={true} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditPartner(null)}
                  className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">
                  Cancel
                </button>
                <button type="submit" disabled={editSaving} data-testid="save-edit-partner-btn"
                  className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
                  {editSaving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
